import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface FilterSelectProps {
  label: string;
  value: string | null;
  allLabel: string;
  options: string[];
  onChange: (value: string | null) => void;
  className?: string;
}

export function FilterSelect({
  label,
  value,
  allLabel,
  options,
  onChange,
  className,
}: FilterSelectProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const active = value != null;

  return (
    <div ref={wrapRef} className={cn('relative inline-block', className)}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'h-6 px-2 rounded border text-[10.5px] font-mono',
          'inline-flex items-center gap-1.5 transition-colors',
          active
            ? 'border-[var(--fm-accent)] bg-accentSoft'
            : 'border-border bg-surfaceAlt hover:border-borderStrong'
        )}
      >
        <span className="text-textMute">{label}</span>
        <span
          className={cn(
            'font-bold',
            active ? 'text-[var(--fm-accent)]' : 'text-text'
          )}
        >
          {value ?? allLabel}
        </span>
        <ChevronDown size={11} className="text-textMute" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 z-30 mt-1 max-h-64 w-48 overflow-auto rounded-md border border-border bg-surface shadow-md"
        >
          <button
            type="button"
            role="option"
            aria-selected={value == null}
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
            className="flex w-full items-center px-3 py-1.5 text-left text-[11.5px] hover:bg-bg"
          >
            {allLabel}
          </button>
          <div className="border-t border-borderSoft" />
          {options.map(opt => (
            <button
              key={opt}
              type="button"
              role="option"
              aria-selected={value === opt}
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center px-3 py-1.5 text-left font-mono text-[11px] hover:bg-bg',
                value === opt ? 'text-[var(--fm-accent)] font-bold' : 'text-text'
              )}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

FilterSelect.displayName = 'FilterSelect';

export default FilterSelect;
